import { Checkbox, Field, NumericInput, RadioGroup, TextOutput, TimeInput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import TimerReadingInput from 'Components/Core/TimerReadingInput/Index';
import { ListFieldSection } from 'Components/Layout';
import { IWorksheetComponent, WorksheetEquipmentDropdown } from 'Components/Worksheets/Core';
import { valueOrDefault } from 'Lib/Any';
import { DateTimeDataFormat, FormatTypes } from 'Lib/Format';
import { pipe } from 'Lib/Hooks/Selectors';
import { useSelector } from 'react-redux';
import {
  After10Min,
  Calcite,
  DispersionAmount,
  DispersionRemould,
  Gypsum,
  Slaking,
  Swelling,
  TimingMethod,
} from '../../types';

let MeasurementsAndObservationsSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const isClockOrWatch = useSelector(
    pipe(createWorksheetSelector!('Preparation.TimingMethod'), (d) => d === TimingMethod.ClockOrWatch)
  ) as boolean;
  const dispersionOccured = useSelector(
    pipe(createWorksheetSelector!('MeasurementsAndObservations.DispersionOccurred'), valueOrDefault(false))
  );
  const slaking = useSelector(createWorksheetSelector!('MeasurementsAndObservations.Slaking')) as Slaking;
  const dispersionAmount = useSelector(
    createWorksheetSelector!('MeasurementsAndObservations.DispersionAmount')
  ) as DispersionAmount;
  const calcite = useSelector(createWorksheetSelector!('MeasurementsAndObservations.Calcite')) as Calcite;
  const gypsum = useSelector(createWorksheetSelector!('MeasurementsAndObservations.Gypsum')) as Gypsum;
  const timePattern = isClockOrWatch ? 'HH:mm' : 'HH:mm:ss';
  return (
    <>
      <NamePrefixModifier appendNamePrefix="MeasurementsAndObservations">
        <ListFieldSection>
          <Field input={<TimerReadingInput />} name="TimerWhenCrumbsWerePlaced" noRecase visible={!!!isClockOrWatch} />
          <Field
            input={<TimeInput />}
            dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
            name="TimeCrumbsWerePlaced"
            visible={isClockOrWatch}
          />
        </ListFieldSection>
        <ListFieldSection heading="Observations">
          <Field
            input={<TextOutput />}
            format={{ pattern: timePattern, type: FormatTypes.Time }}
            name="InitialObservationDue"
          />
          <Field
            input={<TextOutput />}
            name="SecondObservationDue"
            visible={slaking !== Slaking.Within10Min}
            format={{ pattern: timePattern, type: FormatTypes.Time }}
          />
        </ListFieldSection>
        <ListFieldSection>
          <Field
            input={<RadioGroup />}
            name="Slaking"
            options={[
              { label: 'Within 10 minutes', value: Slaking.Within10Min },
              { label: 'Within 2 hours', value: Slaking.Within2Hours },
              { label: 'No', value: Slaking.No },
            ]}
          />
          <Field
            input={<RadioGroup />}
            name="Swelling"
            options={[
              { label: 'Swelling occurred', value: Swelling.SwellingOccurred },
              { label: 'Swelling did not occur', value: Swelling.SwellingDidNotOccur },
            ]}
            visible={slaking === Slaking.No}
          />
          <Field
            input={<RadioGroup />}
            name="DispersionAmount"
            options={[
              { label: 'Extreme', value: DispersionAmount.Extreme },
              { label: 'Strong', value: DispersionAmount.Strong },
              { label: 'Moderate', value: DispersionAmount.Moderate },
              { label: 'Slight', value: DispersionAmount.Slight },
              { label: 'None', value: DispersionAmount.None },
            ]}
            visible={slaking !== Slaking.No}
          />
        </ListFieldSection>
        <ListFieldSection
          heading="Soil Re-moulded"
          visible={slaking !== Slaking.No && dispersionAmount === DispersionAmount.None}
        >
          <Field
            input={<WorksheetEquipmentDropdown />}
            equipmentType="sieve"
            name="MicroSieve"
            queryParams={{ SieveSize: '0.000425 m' }}
          />
          <Field input={<Checkbox />} name="DispersionOccurred" />
          <Field
            input={<RadioGroup />}
            name="DispersionRemould"
            options={[
              { label: 'Extreme', value: DispersionRemould.Extreme },
              { label: 'Strong', value: DispersionRemould.Strong },
              { label: 'Moderate', value: DispersionRemould.Moderate },
              { label: 'Slight', value: DispersionRemould.Slight },
              { label: 'Not recorded', value: DispersionRemould.NotRecorded },
            ]}
            visible={dispersionOccured}
          />
        </ListFieldSection>
        <ListFieldSection
          heading="Testing for Calcite and/or Gypsum"
          visible={!dispersionOccured && slaking !== Slaking.No && dispersionAmount === DispersionAmount.None}
        >
          <Field
            input={<RadioGroup />}
            name="Calcite"
            options={[
              { label: 'Present', value: Calcite.Present },
              { label: 'Not present', value: Calcite.NotPresent },
            ]}
          />
          <Field
            input={<RadioGroup />}
            name="Gypsum"
            options={[
              { label: 'Present', value: Gypsum.Present },
              { label: 'Not present', value: Gypsum.NotPresent },
            ]}
          />
        </ListFieldSection>
        <ListFieldSection
          heading="Prepared As Suspension"
          visible={
            calcite === Calcite.NotPresent &&
            gypsum === Gypsum.NotPresent &&
            !dispersionOccured &&
            slaking !== Slaking.No &&
            dispersionAmount === DispersionAmount.None
          }
        >
          <Field
            input={<RadioGroup />}
            name="After10Min"
            options={[
              { label: 'Suspension remains dispersed', value: After10Min.SuspensionRemainsDispersed },
              { label: 'Flocculation occurred', value: After10Min.FlocculationOccurred },
            ]}
          />
        </ListFieldSection>
        <ListFieldSection heading="Other Information">
          <Field input={<NumericInput />} name="WaterTemperature" />
        </ListFieldSection>
      </NamePrefixModifier>
    </>
  );
};

MeasurementsAndObservationsSection = withInjectedProps(MeasurementsAndObservationsSection);

export { MeasurementsAndObservationsSection };
export default MeasurementsAndObservationsSection;
